import {
  createAppointment,
  createMboClients,
  CreateMboClientsResult,
  createOrUpdateHubSpotContact,
  getCurrentUser,
  getFormSubmissionIpAddress,
  ProfileUpdate,
  saveErrorDetails,
  submitHubSpotForm,
  triggerBackendResync,
} from '@/api/api';
import {
  DEFAULT_FKO_APPT_START_DAYS_FROM_TODAY,
  DEFAULT_FKO_BOOKING_WINDOW,
  DEFAULT_PLAYER_ASSESSMENT_APPT_START_DAYS_FROM_TODAY,
  DEFAULT_PLAYER_ASSESSMENT_BOOKING_WINDOW,
  getLocationBySiteId,
} from '@/constants/locations';
import {FkoInitialParams, FkoPlayer, FkoPlayerWithSession} from '@/redux/slices/fkoFormData';
import {Profile} from '@/user/player-info.interface';
import {createRandomPassword, getCurrentAccountId} from '@/user/user-utils';
import {ApiUser} from '@/user/user.interface';
import {currentDatePlusDays, isDateValid} from '@/utils/utils';
import {createUserWithEmailAndPassword, sendEmailVerification} from 'firebase/auth';
import {DateTime} from 'luxon';
import moment from 'moment';
import {URLSearchParams} from 'url';
import {auth as fireBaseAuth} from '../firebase/config';
import {logSignUpEvent} from './analytics-events';
import {SportName} from './useGetSport';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY!;

export enum FKOPages {
  CONTACT_INFO = 'CONTACT_INFO',
  PLAYERS = 'PLAYERS',
  SCHEDULE = 'SCHEDULE',
  CONFIRMATION = 'CONFIRMATION',
}

export enum FreeTrialProgram {
  PICKUP_SOCCER = 'Adult Pickup Soccer',
  LEAGUES_TRIAL = 'Adult Soccer Leagues',
  FTC_DSR = 'TOCA Soccer Classes',
  DBAT_75_FREE_SWINGS = 'D-BAT 75 Free Swings',
}

export const relevantRegistrationUrlParams = [
  'locationId',
  'trainingProgram',
  'fbclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'embed',
  'date',
  'dateEnd',
  'dailyStartHour',
  'dailyStartMinute',
  'dailyEndHour',
  'dailyEndMinute',
  'teamId',
  'firstname',
  'lastname',
  'email',
  'phone',
  'toca_source',
];

export const relevantFTCDSRUrlParams = [
  'locations__drop_down_',
  'fbclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const relevantFreePickupSoccerAndLeaguesUrlParams = [
  'locations__drop_down_',
  'i_am_interested_in___',
  'source',
  'fbclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'firstname',
  'lastname',
  'email',
  'phone',
  'toca_source',
];

export const relevantDbat75UrlParams = [
  'locations__drop_down_',
  'fbclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const sendFkoHubspotFormData = async ({
  alternateProfileData,
  currentProfile,
  initialParams,
  locationId,
  fkoMethod,
  tocaSource,
  formSubmissionUrl,
  ipAddress: providedIpAddress,
}: {
  alternateProfileData?: ProfileUpdate['update'];
  currentProfile?: Profile;
  initialParams?: FkoInitialParams;
  locationId?: string;
  tocaSource?: string;
  fkoMethod?: string;
  formSubmissionUrl?: string;
  ipAddress?: string | null;
}) => {
  try {
    const formId = import.meta.env.VITE_HUBSPOT_FKO_FORM_ID;
    const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;

    if (!formId || !portalId) {
      console.error('HubSpot form ID or portal ID not configured');
      return;
    }

    const _locationId = locationId || initialParams?.locationId;
    const location = _locationId ? getLocationBySiteId(_locationId) : undefined;

    const rawFormData = {
      firstname: currentProfile?.firstName || alternateProfileData?.firstName,
      lastname: currentProfile?.lastName || alternateProfileData?.lastName,
      email: currentProfile?.email || alternateProfileData?.email,
      phone: currentProfile?.phoneNumber || alternateProfileData?.phoneNumber,
      locations__drop_down_: location?.hubspotName || location?.name || undefined,
      leadsource: initialParams?.toca_source || tocaSource,
      utm_source: initialParams?.utm_source,
      utm_medium: initialParams?.utm_medium,
      gclid: initialParams?.gclid,
      fko_method: fkoMethod,
      utm_campaign: initialParams?.utm_campaign,
      utm_content: initialParams?.utm_content,
      utm_term: initialParams?.utm_term,
      fbclid: initialParams?.fbclid,
      form_submission_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
    };

    // Filter out null/undefined/empty values and cast to proper type
    const formData: Record<string, string | number | boolean> = Object.fromEntries(
      Object.entries(rawFormData).filter(([_, value]) => value != null && value !== '')
    ) as Record<string, string | number | boolean>;

    const ipAddress = providedIpAddress ?? (await getFormSubmissionIpAddress());

    await submitHubSpotForm(formId, portalId, formData, {
      pageUriOverride: formSubmissionUrl,
      ipAddress: ipAddress || undefined,
    });
  } catch (err) {
    console.error('Failed to submit HubSpot form', err);
  }
};

export const sendFkoHubspotData = ({
  alternateProfileData,
  currentProfile,
  initialParams,
  locationId,
  fkoMethod,
  isPlayerAssessment,
  tocaSource,
}: {
  alternateProfileData?: ProfileUpdate['update'];
  currentProfile?: Profile;
  initialParams?: FkoInitialParams;
  locationId?: string;
  tocaSource?: string;
  fkoMethod?: string;
  isPlayerAssessment: boolean;
}) => {
  try {
    const _locationId = locationId || initialParams?.locationId;
    const location = _locationId ? getLocationBySiteId(_locationId) : undefined;

    // hubspot cannot normally support certain playerAssessment-specific location names
    const hubspotLocationParameter = isPlayerAssessment
      ? {mytoca_site: location?.hubspotMyTocaSite ?? location?.hubspotName}
      : {locations__drop_down_: location?.hubspotName || location?.name || undefined};

    const hubspotContactInfo = {
      email: currentProfile?.email || alternateProfileData?.email,
      firstname: currentProfile?.firstName || alternateProfileData?.firstName,
      lastname: currentProfile?.lastName || alternateProfileData?.lastName,
      phone: currentProfile?.phoneNumber || alternateProfileData?.phoneNumber,
      fko_method: fkoMethod,
      fbclid: initialParams?.fbclid,
      gclid: initialParams?.gclid,
      utm_source: initialParams?.utm_source,
      utm_medium: initialParams?.utm_medium,
      utm_campaign: initialParams?.utm_campaign,
      utm_content: initialParams?.utm_content,
      utm_term: initialParams?.utm_term,
      hs_analytics_source: initialParams?.hs_analytics_source,
      toca_source: initialParams?.toca_source || tocaSource,
      ...hubspotLocationParameter,
    };

    createOrUpdateHubSpotContact(hubspotContactInfo).catch((err) => {
      console.error('Failed to create/update HubSpot contact', err);
    });
  } catch (err) {
    console.error('Failed to create/update HubSpot contact', err);
  }
};

type LocationDatesParams = {
  siteId?: string | null;
  dateOverride?: string | null;
  dateOverrideEnd?: string | null;
  isPlayerAssessment?: boolean;
};
// this determines the correct fko booking window, parsing defaults, location-specific settings, and manual overrides
export function getLocationSpecificFkoBookingDates({
  siteId,
  dateOverride,
  dateOverrideEnd,
  isPlayerAssessment,
}: LocationDatesParams) {
  // TODO location-specific booking windows
  const location = siteId ? getLocationBySiteId(siteId) : undefined;

  try {
    if (location) {
      let appointmentsStartDaysFromToday;
      let appointmentsBookingWindowInDays;

      if (location.siteId === '-2147480770') {
        // 'Events' location needs only same-day bookings, temporarily
        appointmentsStartDaysFromToday = 0;
        appointmentsBookingWindowInDays = 1;
      } else {
        appointmentsStartDaysFromToday = isPlayerAssessment
          ? DEFAULT_PLAYER_ASSESSMENT_APPT_START_DAYS_FROM_TODAY
          : location?.fkoAppointmentsStartDaysFromToday ?? DEFAULT_FKO_APPT_START_DAYS_FROM_TODAY;

        appointmentsBookingWindowInDays = isPlayerAssessment
          ? DEFAULT_PLAYER_ASSESSMENT_BOOKING_WINDOW
          : location?.fkoAppointmentsBookingWindowInDays ?? DEFAULT_FKO_BOOKING_WINDOW;
      }

      let startDate;
      let endDate;

      if (dateOverride && isDateValid(dateOverride)) {
        startDate = new Date(dateOverride.replace(/-/g, '/'));
        if (dateOverrideEnd) {
          endDate = isDateValid(dateOverrideEnd) ? new Date(dateOverrideEnd.replace(/-/g, '/')) : new Date(startDate);
          appointmentsBookingWindowInDays = DateTime.fromJSDate(endDate).diff(
            DateTime.fromJSDate(startDate),
            'days'
          ).days;
        } else {
          endDate = DateTime.fromJSDate(startDate).plus({days: appointmentsBookingWindowInDays}).toJSDate();
          appointmentsBookingWindowInDays = 0;
        }
      } else {
        startDate = currentDatePlusDays(appointmentsStartDaysFromToday);
        endDate = DateTime.fromJSDate(startDate).plus({days: appointmentsBookingWindowInDays}).toJSDate();
      }

      const startDateStr = moment(startDate).format().split('T')[0] + 'T00:00:00Z';
      const endDateStr = moment(endDate).format().split('T')[0] + 'T23:59:59Z';

      return {
        bookingWindowInDays: appointmentsBookingWindowInDays,
        startDateStr,
        endDateStr,
        // startDateStr: startDate.toISOString().split('T')[0] + 'T00:00:00Z',
        // endDateStr: endDate.toISOString().split('T')[0] + 'T23:59:59Z',
      };
    } else {
      return undefined;
    }
  } catch {
    console.error('error setting fko dates');
  }
}

export const parseFkoParamsFromSearchParams = (searchParams: URLSearchParams) => {
  const urlParams: any = {};

  for (const urlParamName of relevantRegistrationUrlParams) {
    const value = searchParams.get(urlParamName);
    if (value) {
      urlParams[urlParamName] = value;
    }
  }

  const sessionTypes = searchParams.getAll('sessionTypeNames');
  if (sessionTypes.length) {
    urlParams.sessionTypeNames = sessionTypes;
  }

  return urlParams;
};

export const parseRelevantParamsFromSearchParams = (searchParams: URLSearchParams, relevantUrlParams: string[]) => {
  const urlParams: any = {};
  for (const urlParamName of relevantUrlParams) {
    const value = searchParams.get(urlParamName);
    if (value) {
      urlParams[urlParamName] = value;
    }
  }

  return urlParams;
};

export const createMboClientsForNewFkoPlayers = async ({
  email,
  siteId,
  playersArray,
}: {
  email: string;
  siteId: string;
  playersArray: FkoPlayer[];
}) => {
  const newClients = playersArray
    .filter((player) => player.eligibility?.isEligible && !player.mboDetails?.clientId)
    .map((player) => ({
      clientSideOnlyId: player.playerId,
      accountHolder: player.accountHolder,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: player.firstName!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: player.lastName!,
      birthdate: player.dob,
      gender: player.gender,
      referredBy: player.isDbat ? 'FKO Web - DBAT' : 'FKO Web', // TODBAT confirm
      club: player.club,
    }));

  if (!newClients.length) {
    return new Promise<CreateMboClientsResult>((resolve) => resolve({results: []}));
  }

  return createMboClients({email, siteId, clients: newClients, isFko: true});
};

export const createFkoAppointments = async ({
  players,
  siteId,
  mboAppointmentNotes,
}: {
  players: FkoPlayerWithSession[];
  siteId: string;
  mboAppointmentNotes?: string;
}) => {
  if (!players.length) {
    return [];
  }

  const promises = [];
  for (const player of players) {
    promises.push(
      new Promise<{playerId: string; confirmed: boolean; response?: any; error?: string}>((resolve) => {
        const appointmentData = {
          ClientId: player.mboDetails.clientId,
          LocationId: player.selectedSession.location.id,
          SessionTypeId: player.selectedSession.sessionType.id,
          StaffId: player.selectedSession.staff.id,
          StaffRequested: true,
          SendEmail: true,
          StartDateTime: player.selectedSession.startDate,
          Notes: mboAppointmentNotes,
        };

        createAppointment(siteId, appointmentData, {fko: true})
          .then((response) => {
            resolve({
              playerId: player.playerId,
              confirmed: true,
              response: response,
            });
          })
          .catch((err) => {
            const [errorCode, resyncId] = [err.response?.data?.code, err.response?.data?.resyncTaskId];
            if (errorCode === 'ValidationFailed' && resyncId) {
              triggerBackendResync({resyncId});
            }

            const errorMsg =
              errorCode === 'ValidationFailed'
                ? 'The selected time is not available anymore. Please select a different time.'
                : 'Failed to make reservation';

            resolve({
              playerId: player.playerId,
              confirmed: false,
              error: errorMsg,
            });
          });
      })
    );
  }

  return Promise.all(promises);
};

export const createUser = async (email: string, userGivenPassword?: string) => {
  const password = userGivenPassword || createRandomPassword();
  const createUserResult = await createUserWithEmailAndPassword(fireBaseAuth, email, password);

  logSignUpEvent('emailAndPassword', {
    userUid: createUserResult?.user?.uid,
    operationType: createUserResult?.operationType,
  });

  if (!fireBaseAuth.currentUser.emailVerified) {
    await sendEmailVerification(fireBaseAuth.currentUser);
  }

  return createUserResult;
};

/**
 * YOU SHOULD NOT HAVE TO USE THIS FUNCTION!
 *
 * User accounts are now created immediately via a "precheck" middleware in the API.
 *
 * @deprecated
 */
export const fetchCurrentUserWithRetry = async (
  retryCount = 20
): Promise<
  ApiUser & {
    _pending?: any;
  }
> => {
  const rejectPromiseWithDelay = (error: string) =>
    new Promise((_, reject) => {
      setTimeout(() => reject(error), 1000);
    });

  let count = 0;
  let userProfile: any;

  while (count < retryCount) {
    try {
      userProfile = await getCurrentUser()
        .then(async (res) => {
          const user = res.data;
          const pending = user?._pending;
          if (!user || pending) {
            return rejectPromiseWithDelay('pending');
          } else {
            return user;
          }
        })
        .catch(() => {
          return rejectPromiseWithDelay('error');
        });
    } catch {
      //
    }

    if (userProfile && userProfile !== 'pending' && userProfile !== 'error') {
      count = retryCount;
    } else {
      count++;
    }
  }

  if (!userProfile) {
    void saveErrorDetails({
      key: 'fetchCurrentUserWithRetry',
      message: 'Failed to fetch current user with retry',
      description: '[fetchCurrentUserWithRetry] No user profile returned after max retries',
      severity: 'high',
      context: 'fko',
      accountId: getCurrentAccountId() ?? undefined,
      url: window.location.href,
    });
  }

  return userProfile;
};

export const getPresetSessionTypeNamesBySport = ({sport}: {sport: SportName}) => {
  const FKO_SESSION_TYPE_NAMES = [
    'Free Kick Off Session',
    'Free Kick-Off Session',
    'Free Kick-Off Session - 30 Minutes',
  ];
  const DBAT_SESSION_TYPE_NAMES = ['Evaluation Lesson | 60 Minute', 'Evaluation Lesson | 30 Minute'];
  const PLAYER_ASSESSMENT_SESSION_TYPE_NAMES = ['Player Assessment'];

  const presetSessionTypeNames =
    sport === 'dbat'
      ? DBAT_SESSION_TYPE_NAMES
      : sport === 'playerAssessment'
      ? PLAYER_ASSESSMENT_SESSION_TYPE_NAMES
      : sport === 'toca'
      ? FKO_SESSION_TYPE_NAMES
      : undefined;

  return presetSessionTypeNames;
};
